<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import { numKtoM } from '@/helpers/formating'
import { useQuery } from '@tanstack/vue-query'
import GwSelectNew from '@/components/lib/GwSelectNew/GwSelectNew.vue'
import GwBtnGroup from '@/components/lib/GwBtnGroup/GwBtnGroup.vue'
import GwBox from '@/components/lib/GwBox/GwBox.vue'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import * as R from 'remeda'
import { t, te } from '@/locales/i18n'
import { refDebounced } from '@vueuse/core'
import GwTooltipNew from '@/components/lib/GwTooltip/GwTooltipNew.vue'
import GwText from '@/components/lib/base/GwText/GwText.vue'
import { type SimpleTableDataCommon, type SimpleTableDataResponses } from '@/types/api/hands'
import GwScrollCont from '@/components/lib/GwScrollCont/GwScrollCont.vue'
import { numFixed } from '@/helpers/helpers'
import { sendGaEvent } from '@gto/analytics/google-analytics'
import type { ActionCorrectnessStr } from '@gto/api/vue-query'
import SimpleTableDropdownItem from './SimpleTableDropdownItem.vue'

import {
  simpleTableQueryKey,
  getSimpleTable,
  type FilterParams,
  type TableOption,
} from '../../../../store/api/gql/hands/queries'

const options: TableOption[] = [
  {
    id: 'preflop_actions_table',
    title: t(`hhv3.stats_table.preflop_actions_table.title`),
    text: t(`hhv3.stats_table.preflop_actions_table.text`),
    gql_param: 'pot_type',
    icon: 'mdi-cards-outline',
  },
  {
    id: 'moves_by_street',
    title: t(`hhv3.stats_table.moves_by_street.title`),
    text: t(`hhv3.stats_table.moves_by_street.text`),
    icon: 'mdi-cube',
    gql_param: 'situation_street',
  },
  {
    id: 'positions_table',
    title: t(`hhv3.stats_table.positions_table.title`),
    text: t(`hhv3.stats_table.positions_table.text`),
    icon: 'mdi-account-multiple',
    gql_param: 'player_position',
  },
  {
    id: 'aggressor_defender_table',
    title: t(`hhv3.stats_table.aggressor_defender_table.title`),
    text: t(`hhv3.stats_table.aggressor_defender_table.text`),
    gql_param: 'player_aggression_preflop',
    icon: 'mdi-drama-masks',
  },
]

const tableVariants = [
  { id: 'simple', text: 'Simple' },
  { id: 'complex', text: 'Complex' },
]

const props = defineProps<{
  filterParams: FilterParams
}>()

export type SelectFilterParams = {
  gqlParam: TableOption['gql_param']
  row: keyof SimpleTableDataCommon
  cell: ActionCorrectnessStr
}

export type SimpleTableRow = {
  id: string
  name: string
  cells: {
    id: keyof SimpleTableDataCommon | 'total'
    filter_id?: ActionCorrectnessStr
    value: string | number
  }[]
}

const emit = defineEmits<{
  'cell:click': [SelectFilterParams]
}>()

const selectedTable = ref(options[0])
const filterParams = toRef(props, 'filterParams')

const queryKey = computed(() => [...simpleTableQueryKey, filterParams.value, selectedTable])

const commonInitialData = {
  best_moves: 0,
  correct_moves: 0,
  inaccuracies: 0,
  mistake_moves: 0,
  blunders: 0,
}
const initialData: SimpleTableDataResponses['moves_by_street'] = {
  moves_by_street: [
    { situation_street: 'all_streets', ...commonInitialData },
    { situation_street: 'preflop', ...commonInitialData },
    { situation_street: 'flop', ...commonInitialData },
    { situation_street: 'turn', ...commonInitialData },
    { situation_street: 'river', ...commonInitialData },
  ],
}

const { data, isLoading, isFetching, isFetched } = useQuery({
  queryKey: refDebounced(queryKey, 300),
  refetchOnWindowFocus: false,
  queryFn: getSimpleTable({ filterParams, table: selectedTable }),
  initialData,
  placeholderData: R.identity(),
})

const tableData = computed(() => {
  const id = Object.keys(data.value || {})[0]
  return data.value[id]
})

const rows = computed((): SimpleTableRow[] => {
  const id = Object.keys(data.value || {})[0]
  const qglParam = options.find(o => o.id === id)!.gql_param

  return (tableData.value || []).map(tData => {
    const total =
      tData.best_moves +
      tData.blunders +
      tData.correct_moves +
      tData.inaccuracies +
      tData.mistake_moves

    const getPercentage = (value: number) => `${numFixed((value / total) * 100 || 0, 1)}%`

    return {
      id: tData[qglParam],
      name: te(
        `hhv3.stats_table.${qglParam}.${tData[qglParam].toLowerCase()}`,
        tData[qglParam].toLowerCase(),
      ),
      cells: [
        {
          id: 'total',
          value: numKtoM(total),
        },
        {
          id: 'best_moves',
          filter_id: 'BEST_MOVE',
          value: getPercentage(tData.best_moves),
        },
        {
          id: 'correct_moves',
          filter_id: 'CORRECT_MOVE',
          value: getPercentage(tData.correct_moves),
        },
        {
          id: 'inaccuracies',
          filter_id: 'INACCURACY',
          value: getPercentage(tData.inaccuracies),
        },
        {
          id: 'mistake_moves',
          filter_id: 'WRONG_MOVE',
          value: getPercentage(tData.mistake_moves),
        },
        {
          id: 'blunders',
          filter_id: 'BLUNDER',
          value: getPercentage(tData.blunders),
        },
      ],
    }
  })
})

const handleOptionChange = (item: TableOption) => {
  selectedTable.value = item
}

const headOptions = [
  { title: t('hhv3.stats_table.head.total'), color: 'white' },
  { title: t('hhv3.stats_table.head.perfect'), color: 'best_move' },
  { title: t('hhv3.stats_table.head.good'), color: 'correct_move' },
  { title: t('hhv3.stats_table.head.innacurate'), color: 'inaccuracy' },
  { title: t('hhv3.stats_table.head.mistake'), color: 'mistake' },
  { title: t('hhv3.stats_table.head.blunder'), color: 'blunder' },
]
</script>
<template>
  <!-- TODO error message state -->
  <GwBox
    variant="box_gradient180_bordered"
    :class="{ gw_loading: isFetching && !isFetched && !isLoading }"
    :loading="isLoading"
    style="border-radius: var(--app-rad-4)"
    class="f-column"
  >
    <div class="f-column flex-1">
      <div
        class="d-flex flex-1"
        style="padding: var(--app-pad-5); gap: var(--app-gap-2)"
      >
        <div
          class="f-column"
          style="gap: var(--app-gap-2)"
        >
          <div
            class="no-shrink f-center"
            style="min-height: 3rem"
          >
            <GwSelectNew
              class="mr-auto"
              :title="selectedTable.title"
              style="margin: 0 1rem 0 0; min-width: 11rem; max-width: 11rem"
            >
              <template #content="{}">
                <SimpleTableDropdownItem
                  v-for="item in options"
                  :key="item.id"
                  class="w-100"
                  :item
                  :active="selectedTable.id === item.id"
                  @click="handleOptionChange(item)"
                />
              </template>
            </GwSelectNew>
          </div>

          <GwBox
            v-if="!tableData && !isLoading && !isFetching"
            variant="box_gradient180"
            class="flex-1"
          ></GwBox>
          <div
            v-for="row in rows"
            :key="row.id"
            class="d-flex"
          >
            <GwText
              variant="text_grey_medium_700_uppercase"
              style="margin: 0 1rem 0 0; min-width: 8rem; max-width: 8rem; min-height: 3rem"
              class="f-center no-shrink gw_loading_text"
            >
              <span class="mr-auto">
                {{ row.name }}
              </span>
            </GwText>
          </div>
        </div>

        <GwScrollCont
          direction="horizontal"
          hasOverflowSurfaces
          hasResizeObserver
          class="w-100 flex-1"
        >
          <div
            class="f-column flex-1"
            style="gap: var(--app-gap-2); --cell-width: 5rem"
          >
            <div
              class="w-100 d-flex"
              style="gap: var(--app-gap-2); border-bottom: solid 1px rgba(255, 255, 255, 0.1)"
            >
              <GwText
                v-for="item in headOptions"
                :key="item.title"
                style="min-height: 3rem; min-width: var(--cell-width)"
                :style="`color: var(--app-clr-${item.color})`"
                variant="text_white_medium_700"
                class="flex-1 f-center uppercase w-100 gw_loading_text"
              >
                {{ item.title }}
              </GwText>
            </div>

            <GwBox
              v-if="!tableData && !isLoading && !isFetching"
              variant="box_gradient180"
              class="flex-1"
            ></GwBox>
            <div
              v-for="row in rows"
              :key="row.id"
              class="d-flex w-100"
            >
              <div
                class="w-100 d-flex"
                style="gap: var(--app-gap-2)"
              >
                <div
                  v-for="cell in row.cells"
                  :key="cell.id"
                  style="min-height: 3rem; min-width: var(--cell-width)"
                  class="flex-1 f-center gw_loading_text"
                >
                  <GwText
                    v-if="selectedTable.id === 'moves_by_street'"
                    variant="text_white_medium_700"
                  >
                    {{ cell.value }}
                  </GwText>
                  <GwTooltipNew
                    v-else
                    placement="right"
                  >
                    <template #opener="{ listeners }">
                      <GwBtn
                        :key="cell.id"
                        variant="btn_text_white"
                        style="--text-weight: 700; max-width: 3rem; height: 3rem"
                        class="flex-1 f-center gw_loading_text justify-center"
                        v-on="listeners"
                      >
                        {{ cell.value }}
                      </GwBtn>
                    </template>
                    <template #tooltip>
                      <div
                        class="f-center ga-1 px-1 py-2 clr-primary cursor-pointer"
                        @click="
                          sendGaEvent({
                            event: 'alz_view_these_hands_clicked',
                          }),
                            emit('cell:click', {
                              gqlParam: selectedTable.gql_param,
                              row: row.id as keyof SimpleTableDataCommon,
                              cell: cell.filter_id!,
                            })
                        "
                      >
                        <i class="mdi mdi-eye"></i>
                        <span>{{ t('hhv3.tooltips.view_these_hands') }}</span>
                      </div>
                    </template>
                  </GwTooltipNew>
                </div>
              </div>
            </div>
          </div>
        </GwScrollCont>
      </div>

      <GwBox
        variant="box_light5"
        style="border-top-left-radius: 0px; border-top-right-radius: 0px"
      >
        <div
          class="d-flex"
          style="padding: var(--app-pad-2)"
        >
          <GwTooltipNew
            :tooltip="t('common.coming_soon')"
            placement="right"
          >
            <template #opener="{ listeners }">
              <GwBtnGroup
                class="mr-auto"
                boxVariant="box_row_dark2_pad2"
                direction="horizontal"
                :btnProps="{
                  variant: 'btn_text_grey_white',
                  disabled: true,
                }"
                style="height: 2.125rem"
                :options="tableVariants"
                value="simple"
                isSingle
                v-on="listeners"
              >
                <template #default="{ item }">
                  {{ item.text }}
                </template>
              </GwBtnGroup>
            </template>
          </GwTooltipNew>

          <GwTooltipNew
            :tooltip="t('common.coming_soon')"
            placement="right"
          >
            <template #opener="{ listeners }">
              <GwBtn
                disabled
                class="my-auto"
                variant="btn_text_grey_white"
                icon="mdi-plus-circle"
                v-on="listeners"
                >{{ t('hhv3.add_table') }}</GwBtn
              >
            </template>
          </GwTooltipNew>
        </div>
      </GwBox>
    </div>
  </GwBox>
</template>
